import { Component, OnInit, Input, ViewChild, AfterViewInit, SimpleChanges, ViewChildren, QueryList, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, merge, of, race } from 'rxjs';
import { map, take, first } from 'rxjs/operators';

import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportAspectGroupViewModel } from '../../model/reportAspectGroupViewModel';
import { ReportService } from '../../api/report.service';
import { ReportAspectGroupDetailsComponent } from '../report-aspect-group-details/report-aspect-group-details.component';
import { ReportAspectTextListComponent } from '../report-aspect-text-list/report-aspect-text-list.component';
import { ReportProposalListComponent } from '../report-proposal-list/report-proposal-list.component';

@Component({
    selector: 'app-report-aspect-group-list',
    templateUrl: './report-aspect-group-list.component.html',
    styleUrls: ['./report-aspect-group-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportAspectGroupListComponent implements OnInit {
    @Input() id: number;
    @Input() reportIsDirty: boolean = false;
    @Input() dirtyMessage: string;
    @Input() hasPermissionToChange$: Observable<boolean>;
    @Output() refreshRequest: EventEmitter<any> = new EventEmitter();

    loadingIndicator: boolean;
    displayedColumns = ['name', 'score', 'remainingAspects', 'actions'];
    dataSource: MatTableDataSource<AspectGroupInfo>;


    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private reportService: ReportService,
        private snackBar: MatSnackBar,
        public dialog: MatDialog) {

        this.dataSource = new MatTableDataSource<AspectGroupInfo>();

    }

    ngOnInit() {
        this.loadData();
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.reportService.getAspectGroupsByReportId(this.id).subscribe(
            reqs => this.onDataLoadSuccessful(reqs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(aspectGroups: ReportAspectGroupViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = aspectGroups.map(t => new AspectGroupInfo(t));
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }

    public replaceNull(value: any): any {
        if (value == null) {
            return '';
        }

        return value;
    }

    public showDetails(event: Event, reportAspectGroup: AspectGroupInfo): void {
        this.okToOpenDialog().subscribe((ok) => {
            if (ok) {
                let config = new MatDialogConfig();
                config.data = {
                    reportAspectGroupId: reportAspectGroup.reportAspectGroupId,
                    reportAspectGroupName: reportAspectGroup.name,
                    reportId: reportAspectGroup.reportId,
                    hasPermissionToChange$: this.hasPermissionToChange$
                };
                //config.minWidth = "900px";
                config.disableClose = true;
                event.stopPropagation();

                let dialogRef = this.dialog.open(ReportAspectGroupDetailsComponent, config);
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        this.loadData();
                    }
                });
            }
        });
    }

    public showAspectTexts(event: Event, reportAspectGroup: AspectGroupInfo): void {
        this.okToOpenDialog().subscribe((ok) => {
            if (ok) {
                let config = new MatDialogConfig();
                config.data = {
                    reportAspectGroupId: reportAspectGroup.reportAspectGroupId,
                    reportAspectGroupName: reportAspectGroup.name,
                    hasPermissionToChange$: this.hasPermissionToChange$
                };
                //config.minWidth = "900px";
                config.disableClose = true;
                event.stopPropagation();

                let dialogRef = this.dialog.open(ReportAspectTextListComponent, config);
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        this.refreshRequest.emit();
                        this.loadData();
                    }
                });
            }
        });
    }

    public showProposals(event: Event, reportAspectGroup: AspectGroupInfo): void {
        this.okToOpenDialog().subscribe((ok) => {
            if (ok) {
                let config = new MatDialogConfig();
                config.data = {
                    reportAspectGroupId: reportAspectGroup.reportAspectGroupId,
                    reportAspectGroupName: reportAspectGroup.name,
                    reportId: reportAspectGroup.reportId,
                    hasPermissionToChange$: this.hasPermissionToChange$
                };
                config.disableClose = true;
                config.minWidth = "800px";
                config.minHeight = "600px";
                event.stopPropagation();

                let dialogRef = this.dialog.open(ReportProposalListComponent, config);
            }
        });
    }

    public getScoreText(aspectGroup: ReportAspectGroupViewModel): string {
        if (aspectGroup.scoreLevel != null && aspectGroup.scoreLevel >= 0) {
            return aspectGroup.scoreLevel + " - " + aspectGroup.scoreLevelName;
        } else {
            return aspectGroup.scoreLevelName;
        }
    }

    // Prevent opening of child dialogs if the form is dirty
    okToOpenDialog(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.reportIsDirty) {
            let snackbar = this.snackBar.open(this.dirtyMessage,
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return race(close, remain);
        } else {
            return of(true);
        }
    }

}

export class AspectGroupInfo {

    constructor(aspectGroup: ReportAspectGroupViewModel) {
        this.reportAspectGroupId = aspectGroup.reportAspectGroupId;
        this.name = aspectGroup.name;
        this.reportId = aspectGroup.reportId;
        this.score = aspectGroup.scoreLevel != null && aspectGroup.scoreLevel >= 0
            ? aspectGroup.scoreLevel + " - " + aspectGroup.scoreLevelName
            : aspectGroup.scoreLevelName;
        this.remainingAspects = aspectGroup.unassignedAspectCount + "/" + aspectGroup.assignedAspectCount;
        this.comments = aspectGroup.comments;
    }
    reportAspectGroupId: number;
    name: string;
    score: string;
    remainingAspects: string;
    reportId: number;
    comments: string;
}
